import { Redirect } from "@redotech/react-router-util/redirect";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { OrderManagementPermissions } from "@redotech/redo-model/permissions";
import { Navigate, matchPath } from "react-router-dom";
import { orderFulfillmentAnalyticsRoutes } from "../../../analytics/order-fulfillment/route";
import {
  backwardsCompatibleOrderRoutes,
  outboundLabelRoutes,
  wmsRoutes,
} from "../../../order/route";
import { registrationRoutes } from "../../../registration/route";
import {
  claimRoutes,
  recycleRoutes,
  returnRoutes,
  warrantyRoutes,
} from "../../../return/route";
import { Page } from "../../page";

const HIDE_PADDING_ROUTES: string[] = [
  "/orders",
  "/returns/*",
  "/registrations/*",
  "/claims/*",
  "/warranties/*",
  "/recycle/*",
  "/batch-history",
  "/bulk-fulfillments",
  "/eod-manifest",
  "/outbound-labels/*",
  "/orders/:orderId",
  "/recommerce/*",
  "/analytics/order-fulfillment",
  "/outbound-labels/bad-warehouse-bins",
  "/wms/*",
];

function hidePadding(url: string): boolean {
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/order-management${route}`, url),
  );
}

const FULLSCREEN_ROUTES: string[] = [
  "/outbound-labels/packing-slips/:packingSlipId",
  "/outbound-labels/personalized-slips/:personalizedSlipId",
  "/outbound-labels/pick-sessions",
  "/outbound-labels/pick-sessions/:pickSessionId",
  "/outbound-labels/pick-sessions/:pickSessionId/tote-scanning",
  "/outbound-labels/create-pick-session",
];

const HIDE_HEADER_ROUTES: string[] = [...FULLSCREEN_ROUTES];
function hideHeader(url: string): boolean {
  return HIDE_HEADER_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/order-management${route}`, url),
  );
}

const HIDE_NAV_ROUTES: string[] = [...FULLSCREEN_ROUTES];

function hideNav(url: string): boolean {
  return HIDE_NAV_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/order-management${route}`, url),
  );
}

export const orderManagementRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Order management" },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <Page
            hideHeader={hideHeader}
            hideNavbar={hideNav}
            hidePadding={hidePadding}
          />
        ),
      };
    },
    children: [
      { index: true, element: <Navigate replace to="outbound-labels" /> },
      {
        path: "warranties",
        children: warrantyRoutes,
        handle: { permission: OrderManagementPermissions.MANAGE_WARRANTY },
      },
      {
        path: "returns",
        children: returnRoutes,
        handle: { permission: OrderManagementPermissions.VIEW_RETURN },
      },
      {
        path: "claims",
        children: claimRoutes,
        handle: {
          permission: [
            OrderManagementPermissions.MANAGE_CARRIER_CLAIM,
            OrderManagementPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
          ],
        },
      },
      {
        path: "registrations",
        children: registrationRoutes,
        handle: { permission: OrderManagementPermissions.MANAGE_WARRANTY },
      },
      {
        path: "recycle",
        children: recycleRoutes,
        handle: {
          permission: OrderManagementPermissions.MANAGE_RECYCLING_REQUESTS,
        },
      },
      {
        path: "classify-products",
        handle: { breadcrumb: "Classify products" },
        async lazy() {
          const { ClassifyProductsPage } = await import(
            "../../../order/classify-products-page"
          );
          return { element: <ClassifyProductsPage /> };
        },
      },
      {
        path: "order-edits",
        handle: {
          breadcrumb: "Order edits",
          permission: OrderManagementPermissions.VIEW_ORDER,
        },
        async lazy() {
          const { OrderEditsPage } = await import(
            "../../../order-edits/order-edits-page"
          );
          return { element: <OrderEditsPage /> };
        },
      },
      {
        path: "analytics/order-fulfillment",
        children: orderFulfillmentAnalyticsRoutes,
      },
      { path: "outbound-labels", children: outboundLabelRoutes },
      { path: "wms", children: wmsRoutes },
    ],
  },
];

export const backwardsCompatibleOrderManagementRoutes: PermissionedRouteObject[] =
  [
    {
      path: "approvals",
      children: [
        {
          element: <Page />,
          children: [
            {
              index: true,
              element: (
                <Redirect
                  match={/approvals/}
                  replaceValue="order-management/approvals"
                />
              ),
            },
            {
              path: "*",
              element: (
                <Redirect
                  match={/approvals/}
                  replaceValue="order-management/approvals"
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: "warranties",
      children: [
        {
          element: <Page />,
          children: [
            {
              index: true,
              element: (
                <Redirect
                  match={/warranties/}
                  replaceValue="order-management/warranties"
                />
              ),
            },
            {
              path: "*",
              element: (
                <Redirect
                  match={/warranties/}
                  replaceValue="order-management/warranties"
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: "returns",
      children: [
        {
          element: <Page />,
          children: [
            {
              index: true,
              element: (
                <Redirect
                  match={/(?<!order-management\/)returns/}
                  replaceValue="order-management/returns"
                />
              ),
            },
            {
              path: "*",
              element: (
                <Redirect
                  match={/(?<!order-management\/)returns/}
                  replaceValue="order-management/returns"
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: "about-oms",
      children: [
        {
          element: <Page />,
          children: [
            {
              index: true,
              element: (
                <Redirect
                  match={/about-oms/}
                  replaceValue="order-management/outbound-labels/about-oms"
                />
              ),
            },
            {
              path: "*",
              element: (
                <Redirect
                  match={/about-oms/}
                  replaceValue="order-management/outbound-labels/about-oms"
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: "claims",
      children: [
        {
          element: <Page />,
          children: [
            {
              index: true,
              element: (
                <Redirect
                  match={/claims/}
                  replaceValue="order-management/claims"
                />
              ),
            },
            {
              path: "*",

              element: (
                <Redirect
                  match={/claims/}
                  replaceValue="order-management/claims"
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: "recycle",
      children: [
        {
          element: <Page />,
          children: [
            {
              index: true,
              element: (
                <Redirect
                  match={/recycle/}
                  replaceValue="order-management/recycle"
                />
              ),
            },
            {
              path: "*",
              element: (
                <Redirect
                  match={/recycle/}
                  replaceValue="order-management/recycle"
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: "orders/*",
      element: <Page />,
      children: [{ children: backwardsCompatibleOrderRoutes }],
    },
  ];
